import type { SelfStudyAssignmentPayload, SelfStudyAssignmentSourceType } from './offline-class';

export enum SelfStudyAssignmentStatus {
  GENERATING = 'generating',
  READY = 'ready',
  PUBLISHED = 'published',
  FAILED = 'failed',
}

export type SelfStudyAssignmentActivity = 'reading' | 'vocabulary' | 'grammar';

export type SelfStudyAssignmentDifficulty = 'A1' | 'A2' | 'B1' | 'B2' | 'C1';

// Curriculum-aligned assignments reuse a pre-authored lesson and carry no
// skill or difficulty.
export type SelfStudyAssignmentTopicSource = 'own' | 'curriculum';

export interface SelfStudyAssignmentMetadata {
  duration: number;
  topic: string;
  activity?: SelfStudyAssignmentActivity;
  difficulty?: SelfStudyAssignmentDifficulty;
  description: string;
  topicSource?: SelfStudyAssignmentTopicSource;
}

export interface SelfStudyAssignmentClass {
  id: string;
  name: string | null;
  subject: string | null;
  grade: number | null;
  studentCount: number | null;
}

export interface SelfStudyAssignmentStudent {
  studentId: string;
  profile: { firstName: string | null; lastName: string | null } | null;
}

export enum SelfStudyProgressStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}

// `percentComplete` and `timeSpentSec` are 0 rather than null for a student who
// has not started, so the columns stay numerically sortable without null handling.
export interface SelfStudyAssignmentStudentProgress {
  studentId: string;
  profile: { firstName: string | null; lastName: string | null } | null;
  status: SelfStudyProgressStatus;
  percentComplete: number;
  timeSpentSec: number;
  completedAt: string | null;
}

export interface SelfStudyAssignmentProgress {
  completedCount: number;
  totalCount: number;
  students: SelfStudyAssignmentStudentProgress[];
}

// Assignment report - Gipfeli `selfStudyAssignmentReport(id)` and
// `refreshSelfStudyAssignmentReport(id)` (backend/node/tf/src/schema.graphql, EFEKTA-20160).

/** The average over the students who have a value, and how many that is; null when none do. */
export interface SelfStudyAssignmentReportAverage {
  average: number | null;
  count: number;
}

export interface SelfStudyAssignmentReportAggregates {
  /** The number of students on the assignment. */
  studentCount: number;
  completedCount: number;
  /** In progress or completed. */
  startedCount: number;
  completionPct: SelfStudyAssignmentReportAverage;
  timeSpentSec: SelfStudyAssignmentReportAverage;
  /** Right answers out of the quiz questions, 0-100. */
  quizScore: SelfStudyAssignmentReportAverage;
  /** The most quiz questions any student's attempt has, so the class average reads out of the lesson's questions. */
  quizTaskCount: number | null;
  /** Average stars, 0-3. */
  aiConversationScore: SelfStudyAssignmentReportAverage;
  /** Average stars, 0-3. */
  aiWritingScore: SelfStudyAssignmentReportAverage;
  /** What the lesson contains; null when its content couldn't be read. */
  lessonTasks: SelfStudyAssignmentLessonTasks | null;
}

/** How many speaking (AI roleplay) and writing-challenge tasks the lesson has. */
export interface SelfStudyAssignmentLessonTasks {
  speaking: number;
  writing: number;
}

/** A student's result on one speaking task: stars from their latest session, or null for "Not done". */
export interface SpeakingTaskResult {
  taskId: string;
  // Null when the student hasn't done that task.
  stars: number | null;
}

/** Every metric is null when the student has no data for it - never 0. */
export interface SelfStudyAssignmentReportStudent {
  studentId: string;
  isAssigned: boolean;
  /** Null only when the assignment is not published. */
  status: SelfStudyProgressStatus | null;
  completionPct: number | null;
  timeSpentSec: number | null;
  completedAt: string | null;
  /** Right answers out of quizTaskCount questions, 0-100; null until a question is marked. */
  quizScore: number | null;
  quizTaskCount: number | null;
  /** The average of the 0-3 stars of the speaking tasks the student has done. */
  aiConversationScore: number | null;
  /** The 0-3 stars the student sees for the writing. */
  aiWritingScore: number | null;
  roleplaySessionId: string | null;
  writingActivityId: string | null;
  /** Every speaking task in the lesson, in lesson order; null when the lesson's content couldn't be read. */
  speakingTasks: SpeakingTaskResult[] | null;
  /** How many writing challenges the lesson has; null when its content couldn't be read. */
  writingTaskCount: number | null;
}

export interface SelfStudyAssignmentReport {
  /** The assignment's id, so Apollo keeps one cached report per assignment. */
  id: string;
  /** When the backend built the report - drives the "Last updated" label. */
  generatedAt: string;
  aggregates: SelfStudyAssignmentReportAggregates;
  /** Only present when the query selects it (the opened card). */
  students?: SelfStudyAssignmentReportStudent[];
}

// Assignment summary - Gipfeli `selfStudyAssignmentSummary(id, language)` (EFEKTA-20161).

export enum SelfStudyAssignmentSummaryStatus {
  READY = 'ready',
  /** Fewer than minCompletedCount students have completed the assignment. */
  NOT_ENOUGH_DATA = 'not_enough_data',
}

export interface SelfStudyAssignmentSummary {
  /** Field names match tf-api's AssignmentSummary response, which is read as-is. */
  assignmentId: string;
  status: SelfStudyAssignmentSummaryStatus;
  /** Null unless the status is READY. */
  summaryText: string | null;
  generatedAt: string | null;
  completedCount: number;
  minCompletedCount: number;
}

export interface StudentAssignmentSummary {
  /** Field names match tf-api's StudentAssignmentSummary response, which is read as-is. */
  assignmentId: string;
  studentId: string;
  summaryText: string;
  generatedAt: string;
}

export interface SelfStudyAssignment {
  id: string;
  teacherId: string;
  class: SelfStudyAssignmentClass | null;
  students: SelfStudyAssignmentStudent[] | null;
  isAllStudents: boolean;
  metadata: SelfStudyAssignmentMetadata | null;
  title: string | null;
  description: string | null;
  acciLessonId: string | null;
  status: SelfStudyAssignmentStatus;
  error: string | null;
  createdAt: string;
  publishedAt: string | null;
  progress: SelfStudyAssignmentProgress | null;
}

export interface SelfStudyAssignmentContentPreview {
  jsonContent: {
    content?: {
      steps?: Array<{
        title?: string;
        activities?: Array<Record<string, unknown>>;
      }>;
    };
  };
}

// createSelfStudyAssignment - mirrors the gipfeli SDL in backend/node/tf/src/mutations.graphql.

export interface CreateSelfStudyAssignmentInput {
  sourceId: string;
  sourceType: SelfStudyAssignmentSourceType;
  acciLessonId: string;
  acciPrompt?: string;
  isAllStudents: boolean;
  teacherId: string;
  studentIds?: string[];
  metadata?: Record<string, unknown>;
  title?: string;
  description?: string;
}

// Caller-facing args: the flat Compass `SelfStudyAssignmentPayload` plus the
// `acciLessonId` the mutation requires, so callers need not reshape their payload.
// `activity`/`difficulty` are optional because a curriculum-aligned assignment
// has neither.
export type CreateSelfStudyAssignmentArgs = Omit<SelfStudyAssignmentPayload, 'activity' | 'difficulty'> & {
  activity?: SelfStudyAssignmentPayload['activity'];
  difficulty?: SelfStudyAssignmentPayload['difficulty'];
  acciLessonId: string;
  acciPrompt?: string;
  topicSource?: SelfStudyAssignmentTopicSource;
  title?: string;
};

export interface CreateSelfStudyAssignmentResponse {
  createSelfStudyAssignment: { lessonId: string };
}
